/**
 * Brief template editor — edits name, description, and the sections list
 * (CRUD + reorder + Required / AI allowed flags). There is no page-level Save
 * button: every discrete, completed action (a drawer's Save, a settled drag
 * reorder, a confirmed delete) fires its own PATCH that diffs sections by id.
 * Publishes via POST /publish, which the BE uses to gate template
 * availability for brief generation. Mounted at
 * `/knowledge-hub/brief-templates/:templateId`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Skeleton,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  ChevronDownIcon,
  MoreVerticalIcon,
  PencilIcon,
  RocketIcon,
  Trash2Icon,
} from 'lucide-react';
import {
  briefTemplateDetailQueryOptions,
  briefTemplateResolutionQuery,
  useDeleteBriefTemplate,
  usePublishBriefTemplate,
  useUpdateBriefTemplate,
  BriefTemplateLevelBadge,
  type BriefTemplateDetail,
  type UpdateBriefTemplateSectionVariables,
} from '@/entities/brief-template';
import { DeepLinkInterstitial } from '@/shared/ui';
import { PageTransition } from '@/shared/ui/animations';
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderBackButton,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { PanelReveal } from '@/shared/ui/PanelReveal';
import { UnsavedChangesModal } from '@/shared/ui/UnsavedChangesModal';
import { ROUTES } from '@/shared/config/routes';
import { useDeepLinkResolution, isTerminalResolution } from '@/shared/hooks';
import {
  buildBriefTemplateBuilderFormSchema,
  buildBriefTemplateBuilderPublishSectionsSchema,
  sectionsBlockSaveForAiInstructions,
} from './BriefTemplateBuilder.schema';
import { BriefSectionDrawer } from './components/BriefSectionDrawer';
import { BriefTemplateDetailsDrawer } from './components/BriefTemplateDetailsDrawer';
import { BriefSectionsBlock } from './components/BriefSectionsBlock';
import { BriefSectionTypePicker } from './components/BriefSectionTypePicker';
import { BriefTemplatePreviewPanel } from './components/BriefTemplatePreviewPanel';
import { BriefTemplateReadOnlyNotice } from './components/BriefTemplateReadOnlyNotice';
import { OrganizationTemplateConfirmDialog } from './components/OrganizationTemplateConfirmDialog';
import { useBriefTemplateLevelAccess } from './hooks/useBriefTemplateLevelAccess';
import {
  createBlankDraft,
  shallowSectionsEqual,
  toCreateDto,
  toSectionDraft,
  type SectionDraft,
} from './lib/sectionDraft';
import type { BriefSectionType } from './lib/sectionTypes';
import { useTranslation } from 'react-i18next';

// SDK types `description` as a JSON document but the form edits a plain
// string. Non-string payloads hydrate as empty so the admin can replace
// them; lift once the rich-text editor lands.
function coerceDescription(raw: BriefTemplateDetail['description'] | undefined): string {
  if (typeof raw === 'string') return raw;
  return '';
}

function hydrateSections(template: BriefTemplateDetail): SectionDraft[] {
  return [...template.sections]
    .sort((a, b) => a.position - b.position)
    .map((section) => toSectionDraft(section));
}

interface SavePayload {
  nextSnapshot: { name: string; description: string };
  nextSections: SectionDraft[];
  sectionsPayload: UpdateBriefTemplateSectionVariables[];
}

/**
 * Saved work the published version does not have yet.
 *
 * Both sides are compared: a section's own `updatedAt` catches an edited or added section,
 * and the template's catches the two changes no surviving section records — renaming the
 * template, and deleting a section, whose row leaves the response entirely.
 *
 * This only reads true because publishing settles `publishedAt` onto the row's `updated_at`
 * (see `BriefTemplatesService.publish`). Without that, the publish write itself — `updatedAt`
 * being an `@UpdateDateColumn` — would leave the template a few microseconds "newer" than its
 * own publish, and every freshly published template would claim to be out of date.
 */
function hasUnpublishedWork(template: BriefTemplateDetail | undefined): boolean {
  if (!template?.publishedAt) return false;
  const publishedAt = new Date(template.publishedAt as unknown as string).getTime();
  if (new Date(template.updatedAt).getTime() > publishedAt) return true;
  return template.sections.some((section) => new Date(section.updatedAt).getTime() > publishedAt);
}

export function BriefTemplateBuilder() {
  const { t } = useTranslation('knowledge');
  const { t: tValidation } = useTranslation('validation');
  const { t: tCommon } = useTranslation('common');

  const params = useParams<{ templateId: string }>();
  const templateId = params.templateId ?? '';
  const navigate = useNavigate();

  // Deep-link seam: resolve the id's owning workspace first and
  // hold the template fetch until resolution settles — under the wrong
  // workspace header it would 404 into the error branch prematurely.
  const resolution = useDeepLinkResolution({
    resolutionQuery: briefTemplateResolutionQuery(templateId),
    enabled: templateId !== '',
  });

  const query = useQuery({
    // The seam's interstitial owns the not-found UX, not the global toast.
    ...briefTemplateDetailQueryOptions(templateId, {
      suppressErrorNotification: true,
    }),
    enabled: templateId !== '' && resolution.status === 'ready',
  });

  const levelAccess = useBriefTemplateLevelAccess(query.data);
  const updateMutation = useUpdateBriefTemplate(templateId);
  const publishMutation = usePublishBriefTemplate(templateId);
  const deleteMutation = useDeleteBriefTemplate(templateId);

  // Baseline for dirty detection; rebased after each successful save.
  const [serverSnapshot, setServerSnapshot] = useState<{ name: string; description: string }>({
    name: '',
    description: '',
  });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sections, setSections] = useState<SectionDraft[]>([]);
  const [sectionsSnapshot, setSectionsSnapshot] = useState<SectionDraft[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingTempId, setEditingTempId] = useState<string | null>(null);
  // Title + description live in a drawer opened from the header's "..." menu
  // (`Edit details`) — matches the prototype, which has no always-visible
  // details card. Closed by default; both fields keep patching page state live
  // per keystroke regardless of the drawer's open/closed state.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Preview panel visibility is purely local — not persisted, not URL-bound.
  // Save / publish do not auto-close it; the toggle button disables instead.
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const togglePreview = useCallback(() => setIsPreviewOpen((open) => !open), []);
  // Idempotent close — `togglePreview` would reopen the panel when fired
  // again during the AnimatePresence exit keyframe (e.g. double-click on
  // the in-panel close button).
  const closePreview = useCallback(() => setIsPreviewOpen(false), []);

  // Background refetches must not clobber unsaved edits — dirty state
  // is the source of truth until the user saves or discards.
  const hasHydratedRef = useRef(false);
  const isDirty = useMemo(
    () =>
      name !== serverSnapshot.name ||
      description !== serverSnapshot.description ||
      !shallowSectionsEqual(sections, sectionsSnapshot),
    [name, description, sections, sectionsSnapshot, serverSnapshot],
  );
  // Declared before the hydration effect on purpose: effects run in declaration order, so the
  // guard below reads this commit's dirty state rather than the previous one's.
  const isDirtyRef = useRef(false);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);
  // Reading dirtiness through the ref keeps it out of the dependency array. Subscribing to it
  // re-ran hydration the moment a save flipped it back to false, overwriting the just-saved
  // sections with the pre-save cache entry — a deleted row reappeared until the refetch landed.
  useEffect(() => {
    if (!query.data) return;
    if (hasHydratedRef.current && isDirtyRef.current) return;
    const nextSnapshot = {
      name: query.data.name,
      description: coerceDescription(query.data.description),
    };
    const nextSections = hydrateSections(query.data);
    setServerSnapshot(nextSnapshot);
    setName(nextSnapshot.name);
    setDescription(nextSnapshot.description);
    setSections(nextSections);
    setSectionsSnapshot(nextSections);
    hasHydratedRef.current = true;
  }, [query.data]);

  // Drop the dirty latch when the route changes so the next template
  // hydrates even if the previous one was dirty.
  useEffect(() => {
    hasHydratedRef.current = false;
  }, [templateId]);

  const liveSections = useMemo(() => sections.filter((section) => !section.isDeleted), [sections]);

  const isPending = updateMutation.isPending || publishMutation.isPending;
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  // Track whether the user has attempted to save the template details, or to
  // publish, so we can show field-level errors without bombarding fresh form
  // state.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [publishAttempted, setPublishAttempted] = useState(false);

  // Schema-driven field errors. Messages live in `BriefTemplateBuilder.schema.ts`
  // alongside their dedicated test, so the canonical strings stay versioned.
  const formValidation = useMemo(
    () => buildBriefTemplateBuilderFormSchema(tValidation).safeParse({ name, description }),
    [name, description, tValidation],
  );
  const sectionsValidation = useMemo(
    () => buildBriefTemplateBuilderPublishSectionsSchema(tValidation).safeParse(liveSections),
    [liveSections, tValidation],
  );

  const fieldErrorMap = useMemo(() => {
    if (formValidation.success) return {} as Record<string, string>;
    const map: Record<string, string> = {};
    for (const issue of formValidation.error.issues) {
      const field = issue.path[0];
      if (typeof field === 'string' && !(field in map)) {
        map[field] = issue.message;
      }
    }
    return map;
  }, [formValidation]);

  const nameError = submitAttempted ? fieldErrorMap.name : undefined;
  const descriptionError = submitAttempted ? fieldErrorMap.description : undefined;
  const sectionsError =
    publishAttempted && !sectionsValidation.success
      ? sectionsValidation.error.issues[0]?.message
      : undefined;

  // Whether a given section list is safe to commit: no blank section name,
  // and no section that turns AI on (or edits its instructions) while leaving
  // `aiInstructions` blank. Deliberately STRICTER than the server (see
  // `sectionsBlockSaveForAiInstructions`'s own doc) so an invalid draft never
  // fires a doomed PATCH — it just stays local until fixed, matching the
  // prototype's own refusal to commit in the same situations.
  const isSectionsListValid = useCallback(
    (sectionsForCommit: SectionDraft[]) => {
      const live = sectionsForCommit.filter((section) => !section.isDeleted);
      const hasEmptyName = live.some((section) => section.name.trim() === '');
      if (hasEmptyName) return false;
      return !sectionsBlockSaveForAiInstructions(live, sectionsSnapshot);
    },
    [sectionsSnapshot],
  );

  // Which section is what stops a list from committing. `isSectionsListValid` answers only
  // yes/no, and a refusal the user cannot see is indistinguishable from the edit having worked
  // until they reload and find it undone.
  const findBlockingSection = useCallback(
    (sectionsForCommit: SectionDraft[]): SectionDraft | undefined => {
      const live = sectionsForCommit.filter((section) => !section.isDeleted);
      return (
        live.find((section) => section.name.trim() === '') ??
        live.find((section) => sectionsBlockSaveForAiInstructions([section], sectionsSnapshot))
      );
    },
    [sectionsSnapshot],
  );

  const saveValid = formValidation.success && isSectionsListValid(sections);
  const publishValid = saveValid && sectionsValidation.success;

  // Matches the prototype's `btIsPublished`: the Publish action greys out once
  // the template is published AND has no pending edits — republishing a clean,
  // already-published template is a no-op.
  /**
   * There is something to publish when the draft has unsaved edits, when the template has
   * never been published, or when it has saved sections the published version lacks. Local
   * dirty state alone is not enough any more: every edit persists as it settles, so `isDirty`
   * is false again by the time the author looks at this button.
   */
  const publishDisabled =
    isPending ||
    (query.data?.status === 'published' && !isDirty && !hasUnpublishedWork(query.data));

  // Catches stray sidebar clicks while dirty — e.g. mid-edit inside an open
  // drawer, before its Save has fired a commit. See the persistence model
  // note above `commitSections`.
  const blocker = useBlocker(isDirty);

  const buildSavePayloadFrom = useCallback(
    (sectionsForCommit: SectionDraft[]): SavePayload => {
      const nextSnapshot = { name: trimmedName, description: trimmedDescription };
      const nextSections: SectionDraft[] = sectionsForCommit
        .filter((section) => !section.isDeleted)
        .map((section, index) => ({ ...section, position: index, isNew: false }));
      const sectionsPayload: UpdateBriefTemplateSectionVariables[] = nextSections.map(
        (section, index) => toCreateDto(section, index),
      );
      return { nextSnapshot, nextSections, sectionsPayload };
    },
    [trimmedName, trimmedDescription],
  );

  const rebaseSnapshot = useCallback(
    (nextSnapshot: { name: string; description: string }, nextSections: SectionDraft[]) => {
      setServerSnapshot(nextSnapshot);
      setSections(nextSections);
      setSectionsSnapshot(nextSections);
      // A new row's synthetic `new-<uuid>` tempId does not survive the
      // post-save refetch, so nothing is left open to edit.
      setEditingTempId(null);
    },
    [],
  );

  /**
   * PERSISTENCE MODEL — no page-level Save button. Instead, every discrete,
   * completed user action fires (at most) one PATCH of its own:
   *   - the section drawer's Save button (`handleSaveDrawer`)
   *   - the template-details drawer's Save button (`handleSaveDetails`)
   *   - a settled drag reorder (`handleReorderSections`)
   *   - a confirmed section delete (`handleDeleteSection`)
   * There is no debounced/timer-driven autosave and no effect that commits on
   * every render — each of the four call sites above calls `commitSections`
   * directly from its own event handler.
   *
   * `commitSections` reports what it DID, in three outcomes:
   *   - `committed`: nothing was dirty, or a valid payload was handed to the mutation. The
   *     caller may close its drawer and apply its change.
   *   - `refused`: the payload is invalid (blank section name, blank `aiInstructions` while AI
   *     is allowed, or blank name/description). Nothing goes over the wire, matching the
   *     prototype's own refusal in the same situations. The caller must not close and must
   *     call `revealCommitRefusal`, or the user is left with an error they cannot see.
   *   - `busy`: a save is already in flight, so nothing was sent for THIS change. The caller
   *     must not apply it locally either — the running mutation rebases the snapshot on
   *     success and would erase it.
   *
   * Only "Save" triggers a network write. "Close" (button, Escape, or the
   * overlay) on either drawer always dismisses without persisting — a
   * deliberate divergence from the prototype, where Close and Save are the
   * same handler. Firing a PATCH from a plain dismissal felt like the wrong
   * default against a real backend with no optimistic locking; only an
   * explicit Save should send a request. This is also why `useBlocker` /
   * `UnsavedChangesModal` stay: closing a drawer without saving — or simply
   * typing inside one that is still open — leaves `isDirty` true with nothing
   * persisted, so a real "navigate away and lose it" window remains.
   */
  /**
   * What a commit attempt did, rather than whether the caller may proceed. The boolean this
   * replaced conflated "saved" with "a save was already running", and callers read the second
   * as the first.
   */
  type CommitOutcome = 'committed' | 'refused' | 'busy';

  const commitSections = useCallback(
    (nextAllSections: SectionDraft[]): CommitOutcome => {
      // `busy` rather than `committed`: a save is already in flight and nothing was sent for
      // THIS change. Reporting success here let callers apply a delete or a reorder locally
      // that no request carried, and the in-flight mutation's `onSuccess` then rebased the
      // snapshot over it — the edit vanished on the next render instead of on reload.
      if (isPending) return 'busy';
      const { nextSnapshot, nextSections, sectionsPayload } = buildSavePayloadFrom(nextAllSections);
      const dirty =
        nextSnapshot.name !== serverSnapshot.name ||
        nextSnapshot.description !== serverSnapshot.description ||
        !shallowSectionsEqual(nextSections, sectionsSnapshot);
      if (!dirty) return 'committed';
      if (!formValidation.success || !isSectionsListValid(nextAllSections)) return 'refused';
      updateMutation.mutate(
        { ...nextSnapshot, sections: sectionsPayload },
        {
          onSuccess: () => {
            rebaseSnapshot(nextSnapshot, nextSections);
            setSubmitAttempted(false);
          },
        },
      );
      return 'committed';
    },
    [
      isPending,
      buildSavePayloadFrom,
      serverSnapshot,
      sectionsSnapshot,
      formValidation.success,
      isSectionsListValid,
      updateMutation,
      rebaseSnapshot,
    ],
  );

  /**
   * Put on screen whatever made `commitSections` refuse, by opening the control that owns it:
   * the details drawer for a blank title or description, the section drawer for the section
   * that blocks. Mirrors what `handlePublish` already does, and is the half `handleSaveDrawer`
   * was missing.
   */
  const revealCommitRefusal = useCallback(
    (attemptedSections: SectionDraft[]) => {
      setSubmitAttempted(true);
      if (!formValidation.success) {
        setDetailsOpen(true);
        return;
      }
      const blocking = findBlockingSection(attemptedSections);
      if (blocking) setEditingTempId(blocking.tempId);
    },
    [formValidation.success, findBlockingSection],
  );

  const handleDeleteSection = useCallback(
    (tempId: string) => {
      const nextAll = sections.map((section) =>
        section.tempId === tempId ? { ...section, isDeleted: true } : section,
      );
      // Committed BEFORE the local state moves. Applying the delete and then discarding a
      // refusal is what made a row vanish from the table with no request sent and nothing
      // said, only to return on the next load.
      const outcome = commitSections(nextAll);
      if (outcome === 'refused') {
        revealCommitRefusal(nextAll);
        return;
      }
      // `busy` drops the action rather than showing it: the controls that reach here are
      // already `disabled` while a save is in flight, so this is the narrow race, and a row
      // that stays put is truer than one that leaves and comes back.
      if (outcome === 'busy') return;
      setSections(nextAll);
    },
    [sections, commitSections, revealCommitRefusal],
  );

  const handleRemoveNewSection = useCallback((tempId: string) => {
    // Client-only row never reached the server — drop it outright instead of
    // tombstoning. No undo path needed because nothing was ever persisted,
    // and nothing needs to be committed for this specific removal.
    setSections((prev) => prev.filter((section) => section.tempId !== tempId));
  }, []);

  const handleReorderSections = useCallback(
    (nextOrder: SectionDraft[]) => {
      // Position rebinds to the live-row index. Tombstones keep their old slot —
      // the save payload strips them, so their position field is moot anyway.
      let livePtr = 0;
      const nextAll = nextOrder.map((section) => {
        if (section.isDeleted) return section;
        const position = livePtr;
        livePtr += 1;
        return { ...section, position };
      });
      const outcome = commitSections(nextAll);
      if (outcome === 'refused') {
        revealCommitRefusal(nextAll);
        return;
      }
      if (outcome === 'busy') return;
      setSections(nextAll);
    },
    [commitSections, revealCommitRefusal],
  );

  // The type picker and section drawer never mutate `sections` directly —
  // they always go through these.
  const handleOpenTypePicker = useCallback(() => setPickerOpen(true), []);

  const handlePickSectionType = useCallback((type: BriefSectionType) => {
    const draft = createBlankDraft(0, type);
    setPickerOpen(false);
    setEditingTempId(draft.tempId);
    setSections((prev) => {
      const live = prev.filter((section) => !section.isDeleted);
      return [...prev, { ...draft, position: live.length }];
    });
  }, []);

  const handleOpenSection = useCallback((tempId: string) => setEditingTempId(tempId), []);

  const handleCloseDrawer = useCallback(() => setEditingTempId(null), []);

  const handleSaveDrawer = useCallback(() => {
    if (commitSections(sections) === 'refused') {
      revealCommitRefusal(sections);
      return;
    }
    setEditingTempId(null);
  }, [commitSections, sections, revealCommitRefusal]);

  const handlePatchSection = useCallback((tempId: string, patch: Partial<SectionDraft>) => {
    setSections((prev) =>
      prev.map((section) => (section.tempId === tempId ? { ...section, ...patch } : section)),
    );
  }, []);

  const editingSection = useMemo(
    () => sections.find((section) => section.tempId === editingTempId) ?? null,
    [sections, editingTempId],
  );

  // The open section as the server last gave it to us. The drawer needs it to grandfather a
  // section nobody edited, exactly as the save gate does.
  const editingPersistedSection = useMemo(
    () => sectionsSnapshot.find((section) => section.tempId === editingTempId),
    [sectionsSnapshot, editingTempId],
  );

  // After publish lands, re-seed the dirty baseline from the server response
  // so sectionsSnapshot matches the BE's freshly-assigned section ids (BE
  // deletes-and-recreates on PATCH). Without this the post-publish state
  // races with the refetch and Save can re-arm.
  const handlePublishSuccess = useCallback(
    (data: BriefTemplateDetail) => {
      rebaseSnapshot(
        { name: data.name, description: coerceDescription(data.description) },
        hydrateSections(data),
      );
      setSubmitAttempted(false);
      setPublishAttempted(false);
    },
    [rebaseSnapshot],
  );

  const handlePublish = useCallback(() => {
    if (publishDisabled) return;
    setSubmitAttempted(true);
    setPublishAttempted(true);
    if (!formValidation.success) setDetailsOpen(true);
    if (!publishValid) return;
    if (!isDirty) {
      publishMutation.mutate(undefined, { onSuccess: handlePublishSuccess });
      return;
    }
    // Auto-chain save → publish. PATCH first so the published payload reflects
    // the staged edits; publish only fires if save succeeds, so a save failure
    // surfaces the save-error toast and the publish step is skipped silently.
    const { nextSnapshot, nextSections, sectionsPayload } = buildSavePayloadFrom(sections);
    updateMutation.mutate(
      { ...nextSnapshot, sections: sectionsPayload },
      {
        onSuccess: () => {
          rebaseSnapshot(nextSnapshot, nextSections);
          publishMutation.mutate(undefined, { onSuccess: handlePublishSuccess });
        },
      },
    );
  }, [
    publishDisabled,
    publishValid,
    formValidation.success,
    isDirty,
    sections,
    publishMutation,
    updateMutation,
    buildSavePayloadFrom,
    rebaseSnapshot,
    handlePublishSuccess,
  ]);

  const goBackToList = useCallback(() => {
    void navigate(ROUTES.KNOWLEDGE_HUB_BRIEF_TEMPLATES);
  }, [navigate]);

  const handleOpenDetails = useCallback(() => setDetailsOpen(true), []);
  const handleCloseDetails = useCallback(() => setDetailsOpen(false), []);

  const handleSaveDetails = useCallback(() => {
    setSubmitAttempted(true);
    // A refusal here is often about a SECTION, not about the fields this drawer shows, so
    // without this the drawer sat open displaying no error at all.
    if (commitSections(sections) === 'refused') {
      revealCommitRefusal(sections);
      return;
    }
    setDetailsOpen(false);
  }, [commitSections, sections, revealCommitRefusal]);

  const handleOpenDelete = useCallback(() => setDeleteOpen(true), []);
  const handleCancelDelete = useCallback(() => setDeleteOpen(false), []);
  const handleConfirmDelete = useCallback(() => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        setDeleteOpen(false);
        goBackToList();
      },
    });
  }, [deleteMutation, goBackToList]);

  const { withOrganizationConfirm, afterOwnConfirmation } = levelAccess;
  const onDeleteSection = afterOwnConfirmation(handleDeleteSection);
  const onReorderSections = withOrganizationConfirm(handleReorderSections);
  const onSaveDrawer = withOrganizationConfirm(handleSaveDrawer, () => isDirty);
  const onSaveDetails = withOrganizationConfirm(handleSaveDetails, () => isDirty);
  const onPublish = withOrganizationConfirm(handlePublish);
  const editingDisabled = isPending || levelAccess.readOnly;

  if (templateId === '') {
    return (
      <div
        data-testid="brief-template-builder-error-state"
        className="flex flex-col items-center justify-center gap-3 p-12 text-center"
      >
        <p className="text-base font-medium text-foreground">
          {t('knowledgeHub.briefTemplateBuilder.noBriefTemplateSelected')}
        </p>
        <Button variant="outline" onClick={goBackToList}>
          {t('knowledgeHub.briefTemplateBuilder.backToBriefTemplates')}
        </Button>
      </div>
    );
  }

  if (isTerminalResolution(resolution)) {
    return <DeepLinkInterstitial state={resolution} entity="briefTemplate" />;
  }

  // Also covers `resolving`: the held query stays pending until the seam
  // settles on `ready`.
  if (query.isPending) {
    return (
      <div
        data-testid="brief-template-builder-skeleton"
        className="flex h-full flex-col gap-app-inset"
      >
        <Skeleton className="h-14 shrink-0 rounded-lg" />
        <Skeleton className="flex-1 rounded-lg" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        data-testid="brief-template-builder-error-state"
        className="flex flex-col items-center justify-center gap-3 p-12 text-center"
      >
        <p className="text-base font-medium text-foreground">
          {t('knowledgeHub.briefTemplateBuilder.weCouldnTLoadThis')}
        </p>
        <Button variant="outline" onClick={() => void query.refetch()}>
          {t('knowledgeHub.briefTemplateBuilder.retry')}
        </Button>
      </div>
    );
  }

  const template = query.data;
  const lastModifiedLabel = formatDistanceToNow(new Date(template.updatedAt), {
    addSuffix: true,
  });
  const lastPublishedLabel = template.publishedAt
    ? formatDistanceToNow(new Date(template.publishedAt), { addSuffix: true })
    : null;

  const isPublished = template.status === 'published';

  // Drafts are excluded: the status control already says they are not live.
  const hasUnpublishedChanges = isPublished && hasUnpublishedWork(template);

  const publishPopover = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="lg"
          disabled={isPending}
          className={
            isPublished
              ? 'border-transparent bg-positive text-positive-foreground hover:bg-positive/90'
              : undefined
          }
        >
          {isPublished
            ? t('knowledgeHub.briefTemplateBuilder.published')
            : t('knowledgeHub.briefTemplateBuilder.publish')}
          <ChevronDownIcon className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        data-testid="brief-template-publish-popover"
        className="flex w-60 flex-col gap-3"
      >
        <div className="flex flex-col">
          <span className="text-sm font-medium">
            {t('knowledgeHub.briefTemplateBuilder.lastModified')}
          </span>
          <span className="text-sm text-muted-foreground">{lastModifiedLabel}</span>
        </div>
        {lastPublishedLabel && (
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {t('knowledgeHub.briefTemplateBuilder.lastPublished')}
            </span>
            <span className="text-sm text-muted-foreground">{lastPublishedLabel}</span>
          </div>
        )}
        <Separator />
        <Button variant="default" onClick={onPublish} disabled={publishDisabled} className="w-full">
          <RocketIcon className="size-4" />
          {publishMutation.isPending
            ? t('knowledgeHub.briefTemplateBuilder.publishing')
            : t('knowledgeHub.briefTemplateBuilder.publish')}
        </Button>
        {sectionsError && (
          <p
            role="alert"
            className="text-destructive text-xs"
            data-testid="brief-template-sections-error"
          >
            {sectionsError}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="flex h-full flex-col gap-app-inset overflow-y-auto">
      <PageTransition variant="slideUp" delay={0.0}>
        {
          <PageHeader>
            <PageHeaderIdentity>
              <PageHeaderBackButton onClick={goBackToList} />
              <PageHeaderBreadcrumb>
                <PageHeaderBreadcrumbLink href={ROUTES.KNOWLEDGE_HUB_BRIEF_TEMPLATES}>
                  {t('knowledgeHub.briefTemplateBuilder.briefTemplates')}
                </PageHeaderBreadcrumbLink>
                <PageHeaderBreadcrumbPage>{name || template.name}</PageHeaderBreadcrumbPage>
              </PageHeaderBreadcrumb>
            </PageHeaderIdentity>
            <PageHeaderActions>
              {levelAccess.levelsEnabled && levelAccess.level && (
                <BriefTemplateLevelBadge level={levelAccess.level} />
              )}
              {hasUnpublishedChanges && (
                <Badge variant="warning" data-testid="brief-template-unpublished-badge">
                  {t('knowledgeHub.briefTemplateBuilder.unpublishedChanges')}
                </Badge>
              )}
              <Button
                variant={isPreviewOpen ? 'secondary' : 'outline'}
                size="lg"
                onClick={togglePreview}
                aria-pressed={isPreviewOpen}
                disabled={isPending}
              >
                {t('knowledgeHub.briefTemplateBuilder.preview')}
              </Button>
              {!levelAccess.readOnly && publishPopover}
              {!levelAccess.readOnly && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-lg"
                      aria-label={t('knowledgeHub.briefTemplateBuilder.templateActions')}
                      className="data-[state=open]:bg-accent"
                    >
                      <MoreVerticalIcon className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={handleOpenDetails}>
                      <PencilIcon className="mr-2 size-4" />
                      {t('knowledgeHub.briefTemplateBuilder.editDetails')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={handleOpenDelete}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2Icon className="mr-2 size-4" />
                      {t('knowledgeHub.briefTemplateBuilder.deleteTemplate')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </PageHeaderActions>
          </PageHeader>
        }
      </PageTransition>

      {levelAccess.readOnly && <BriefTemplateReadOnlyNotice />}

      {/*
       * Stable flex-row wrapper kept open whether or not the preview panel is
       * mounted: the editor column owns its own vertical scroll (so the Brief
       * sections list scrolls within its track instead of growing the page),
       * and the panel keeps its flex slot through AnimatePresence's exit
       * keyframe — without that slot the closing panel detaches from the row
       * and wraps below the editor mid-animation.
       */}
      <div className="@container flex min-h-0 flex-1">
        {/*
         * `min-w-0` is load-bearing: a flex item defaults to `min-width: auto`,
         * so without it the sections table's min-content width stops this
         * column shrinking and the preview panel loses its half of the row.
         */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-app-inset">
          <PageTransition variant="slideUp" delay={0.1} className="flex min-h-0 flex-1 flex-col">
            <BriefSectionsBlock
              sections={sections}
              disabled={editingDisabled}
              onAdd={handleOpenTypePicker}
              onOpenSection={handleOpenSection}
              onDelete={onDeleteSection}
              onReorder={onReorderSections}
              deleteWarning={
                levelAccess.confirmsWrites
                  ? t('knowledgeHub.briefTemplateOrganizationConfirm.sectionDeleteWarning')
                  : undefined
              }
            />
          </PageTransition>
        </div>
        <PanelReveal
          isOpen={isPreviewOpen}
          axis="width"
          gapEdge="left"
          fadeOnClose
          className="flex min-h-0 shrink-0"
        >
          <BriefTemplatePreviewPanel sections={liveSections} onClose={closePreview} />
        </PanelReveal>
      </div>

      <BriefSectionTypePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handlePickSectionType}
      />
      <BriefSectionDrawer
        section={editingSection}
        persistedSection={editingPersistedSection}
        disabled={editingDisabled}
        onPatch={handlePatchSection}
        onClose={handleCloseDrawer}
        onSave={onSaveDrawer}
        onRemoveIfNew={handleRemoveNewSection}
      />

      <BriefTemplateDetailsDrawer
        open={detailsOpen}
        name={name}
        description={description}
        disabled={editingDisabled}
        nameError={nameError}
        descriptionError={descriptionError}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onClose={handleCloseDetails}
        onSave={onSaveDetails}
      />

      <OrganizationTemplateConfirmDialog {...levelAccess.confirmDialog} />

      <DeleteConfirmationModal
        open={deleteOpen}
        isDeleting={deleteMutation.isPending}
        onCancel={handleCancelDelete}
        onConfirmDelete={handleConfirmDelete}
        resourceType={tCommon('components.deleteConfirmationModal.template')}
        resourceName={name || template.name}
        warningMessage={
          levelAccess.confirmsWrites
            ? t('knowledgeHub.briefTemplateOrganizationConfirm.templateDeleteWarning')
            : t('knowledgeHub.briefTemplatesActionMenu.thisActionCannotBeUndone')
        }
        data-testid="brief-template-delete-modal"
      />

      <UnsavedChangesModal
        open={blocker.state === 'blocked'}
        onCancel={() => blocker.reset?.()}
        onConfirmLeave={() => blocker.proceed?.()}
      />
    </div>
  );
}
